// ============================================================================
// Bases y Propiedades — availability / free-slot computation
// ----------------------------------------------------------------------------
// Mirrors the semantics of the CRM `/bookings/slots` + `/bookings/request`
// logic (apps/v5/src/Layouts/crm/services/bookingsService.ts) but scoped to a
// single amenity instead of the agency agenda:
//
//   free slots = amenity hours − existing reservations − blocks
//
// Pure functions (no React, no store) so they are reused by the staff booker,
// the public Airbnb-style page and any future backend that fills the same
// shapes. Times are wall-clock "HH:mm" within a single calendar day.
// ============================================================================

import type { Amenity, Booking } from "../store";

/** A manual/maintenance block that removes time from an amenity's day. */
export interface AvailabilityBlock {
  amenityId: string;
  date: string; // YYYY-MM-DD
  startTime: string; // HH:mm
  endTime: string; // HH:mm
  reason?: string;
}

/** A free reservable window: [start, end) wall-clock on the requested day. */
export interface FreeSlot {
  start: string; // HH:mm
  end: string; // HH:mm
}

/** "HH:mm" → minutes since midnight. */
export function toMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
}

/** minutes since midnight → "HH:mm". */
export function toHHMM(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/** Do half-open ranges [aStart,aEnd) and [bStart,bEnd) overlap? */
export function rangesOverlap(
  aStart: number,
  aEnd: number,
  bStart: number,
  bEnd: number,
): boolean {
  return aStart < bEnd && bStart < aEnd;
}

/** Active (non-cancelled) bookings for an amenity on a given day. */
export function bookingsForAmenityOnDate(
  bookings: Booking[],
  amenityId: string,
  date: string,
): Booking[] {
  return bookings.filter(
    (b) =>
      b.amenityId === amenityId &&
      b.date === date &&
      b.status !== "Cancelada",
  );
}

/**
 * Find an existing booking that would overlap [startTime, endTime) for the
 * same amenity/day. Used to prevent double-booking. `ignoreId` skips the
 * booking being edited. Returns the conflicting booking or null.
 */
export function findBookingConflict(
  bookings: Booking[],
  amenityId: string,
  date: string,
  startTime: string,
  endTime: string,
  ignoreId?: string,
): Booking | null {
  const s = toMinutes(startTime);
  const e = toMinutes(endTime);
  for (const b of bookings) {
    if (b.id === ignoreId) continue;
    if (b.amenityId !== amenityId || b.date !== date) continue;
    if (b.status === "Cancelada") continue;
    if (rangesOverlap(s, e, toMinutes(b.startTime), toMinutes(b.endTime))) {
      return b;
    }
  }
  return null;
}

export interface ComputeSlotsArgs {
  amenity: Amenity;
  bookings: Booking[];
  date: string; // YYYY-MM-DD
  /** Requested duration in minutes (defaults to the amenity minimum). */
  durationMinutes?: number;
  /** Manual/maintenance blocks (defaults to none). */
  blocks?: AvailabilityBlock[];
}

/**
 * Free start-slots for an amenity on a day:
 *   walk the amenity hours in `minMinutes` steps and keep every window
 *   [t, t+duration) that fits before close and overlaps no booking or block.
 * Amenities in "Mantenimiento" yield no slots.
 */
export function computeFreeSlots({
  amenity,
  bookings,
  date,
  durationMinutes,
  blocks = [],
}: ComputeSlotsArgs): FreeSlot[] {
  if (amenity.status === "Mantenimiento") return [];

  const open = toMinutes(amenity.openTime);
  const close = toMinutes(amenity.closeTime);
  const step = amenity.minMinutes > 0 ? amenity.minMinutes : 30;
  const duration = durationMinutes && durationMinutes > 0
    ? durationMinutes
    : step;
  if (close <= open || duration <= 0) return [];

  const busy: Array<[number, number]> = [
    ...bookingsForAmenityOnDate(bookings, amenity.id, date).map(
      (b) => [toMinutes(b.startTime), toMinutes(b.endTime)] as [number, number],
    ),
    ...blocks
      .filter((bl) => bl.amenityId === amenity.id && bl.date === date)
      .map((bl) => [toMinutes(bl.startTime), toMinutes(bl.endTime)] as [number, number]),
  ];

  const slots: FreeSlot[] = [];
  for (let t = open; t + duration <= close; t += step) {
    const end = t + duration;
    const clash = busy.some(([bs, be]) => rangesOverlap(t, end, bs, be));
    if (!clash) slots.push({ start: toHHMM(t), end: toHHMM(end) });
  }
  return slots;
}

/** Duration options (minutes) allowed by an amenity: min..max in min steps. */
export function durationOptions(amenity: Amenity): number[] {
  const min = amenity.minMinutes > 0 ? amenity.minMinutes : 30;
  const max = amenity.maxMinutes >= min ? amenity.maxMinutes : min;
  const opts: number[] = [];
  for (let d = min; d <= max; d += min) opts.push(d);
  return opts.length ? opts : [min];
}
